import { useState } from 'react';
import { Alert, Button, Group, Stack, Table, Tabs, Text, Title } from '@mantine/core';
import { createFileRoute, Link } from '@tanstack/react-router';
import {
  IconAdjustments,
  IconAdjustmentsOff,
  IconArrowsExchange,
  IconExchangeOff,
  IconHelpCircle,
  IconPackageExport,
  IconPackageOff,
  IconPlus,
} from '@tabler/icons-react';
import { useAuth } from '../../../auth/AuthContext';
import { DocStatusBadge } from '../../../components/DocStatusBadge';
import { EmptyState } from '../../../components/EmptyState';
import { formatDate } from '../../../features/stores-transfers/format';
import { useAdjustmentsList, useStoresIssuesList, useTransfersList } from '../../../features/stores-transfers/api';

/** Owned by G24 (plan.md S0). The stores-transfers register hub: two registers
 * (issues, transfers — BUILD_SPEC.md §11 "Registers with fast fuzzy search +
 * filters") behind tabs, each with a "New" entry point into the guided create flow. */
export const Route = createFileRoute('/_authenticated/stores-transfers/')({
  component: StoresTransfersHub,
});

function StoresTransfersHub() {
  const auth = useAuth();
  const canWrite = auth.activeTenant?.role === 'OPERATIONS' || auth.activeTenant?.role === 'ADMIN';

  return (
    <Stack gap="lg">
      <Group justify="space-between" align="flex-end">
        <Stack gap={2}>
          <Title order={2}>Stores &amp; Transfers</Title>
          <Text c="dimmed">Issue stock to a cost centre, or move stock between locations and sites.</Text>
        </Stack>
      </Group>

      <Alert variant="light" color="graphite" icon={<IconHelpCircle size={16} />} title="Which one do I use?">
        <Stack gap={2}>
          <Text size="sm">
            <strong>Issue</strong> — stock consumed against a cost centre (spares, consumables, general usage).
          </Text>
          <Text size="sm">
            <strong>Transfer</strong> — stock physically moved between locations, same-site or cross-site.
          </Text>
          <Text size="sm">
            <strong>Adjustment</strong> — correcting a quantity with no physical movement (write-off or found stock).
          </Text>
          <Text size="sm">
            <strong>Stocktake</strong> — counting everything and reconciling (Silo &amp; Counts &rarr; Stock counts).
          </Text>
        </Stack>
      </Alert>

      <Tabs defaultValue="issues">
        <Tabs.List>
          <Tabs.Tab value="issues" leftSection={<IconPackageExport size={16} />}>
            Issues
          </Tabs.Tab>
          <Tabs.Tab value="transfers" leftSection={<IconArrowsExchange size={16} />}>
            Transfers
          </Tabs.Tab>
          <Tabs.Tab value="adjustments" leftSection={<IconAdjustments size={16} />}>
            Adjustments
          </Tabs.Tab>
        </Tabs.List>

        <Tabs.Panel value="issues" pt="md">
          <IssuesRegister canWrite={canWrite} />
        </Tabs.Panel>
        <Tabs.Panel value="transfers" pt="md">
          <TransfersRegister canWrite={canWrite} />
        </Tabs.Panel>
        <Tabs.Panel value="adjustments" pt="md">
          <AdjustmentsRegister canWrite={canWrite} />
        </Tabs.Panel>
      </Tabs>
    </Stack>
  );
}

function IssuesRegister({ canWrite }: { canWrite: boolean }) {
  const [cursors, setCursors] = useState<(string | undefined)[]>([undefined]);
  const cursor = cursors[cursors.length - 1];
  const { data, isLoading } = useStoresIssuesList(cursor);

  return (
    <Stack gap="sm">
      <Group justify="flex-end">
        {canWrite && (
          <Button component={Link} to="/stores-transfers/issues/new" leftSection={<IconPlus size={16} />}>
            New issue
          </Button>
        )}
      </Group>
      <Table striped highlightOnHover verticalSpacing="xs">
        <Table.Thead>
          <Table.Tr>
            <Table.Th>Number</Table.Th>
            <Table.Th>Issued</Table.Th>
            <Table.Th>Status</Table.Th>
            <Table.Th>Lines</Table.Th>
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {(data?.items ?? []).map((issue) => (
            <Table.Tr key={issue.id}>
              <Table.Td>
                <Link to="/stores-transfers/issues/$issueId" params={{ issueId: issue.id }}>
                  {issue.number}
                </Link>
              </Table.Td>
              <Table.Td>{formatDate(issue.issuedAt)}</Table.Td>
              <Table.Td>
                <DocStatusBadge status={issue.status} />
              </Table.Td>
              <Table.Td>{issue.lines.length}</Table.Td>
            </Table.Tr>
          ))}
          {!isLoading && (data?.items.length ?? 0) === 0 && (
            <Table.Tr>
              <Table.Td colSpan={4}>
                <EmptyState icon={IconPackageOff} title="No stores issues yet" hint="Issues you create will appear here." />
              </Table.Td>
            </Table.Tr>
          )}
        </Table.Tbody>
      </Table>
      {data?.nextCursor && (
        <Group justify="center">
          <Button variant="subtle" onClick={() => setCursors((prev) => [...prev, data.nextCursor ?? undefined])}>
            Load more
          </Button>
        </Group>
      )}
    </Stack>
  );
}

function AdjustmentsRegister({ canWrite }: { canWrite: boolean }) {
  const [cursors, setCursors] = useState<(string | undefined)[]>([undefined]);
  const cursor = cursors[cursors.length - 1];
  const { data, isLoading } = useAdjustmentsList(cursor);

  return (
    <Stack gap="sm">
      <Group justify="flex-end">
        {canWrite && (
          <Button component={Link} to="/stores-transfers/adjustments/new" leftSection={<IconPlus size={16} />}>
            New adjustment
          </Button>
        )}
      </Group>
      <Table.ScrollContainer minWidth={640}>
        <Table striped highlightOnHover verticalSpacing="xs">
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Number</Table.Th>
              <Table.Th>Reason</Table.Th>
              <Table.Th>Status</Table.Th>
              <Table.Th>Lines</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {(data?.items ?? []).map((adjustment) => (
              <Table.Tr key={adjustment.id}>
                <Table.Td>
                  <Link to="/stores-transfers/adjustments/$adjustmentId" params={{ adjustmentId: adjustment.id }}>
                    {adjustment.number}
                  </Link>
                </Table.Td>
                <Table.Td>{adjustment.reason}</Table.Td>
                <Table.Td>
                  <DocStatusBadge status={adjustment.status} />
                </Table.Td>
                <Table.Td>{adjustment.lines.length}</Table.Td>
              </Table.Tr>
            ))}
            {!isLoading && (data?.items.length ?? 0) === 0 && (
              <Table.Tr>
                <Table.Td colSpan={4}>
                  <EmptyState
                    icon={IconAdjustmentsOff}
                    title="No stock adjustments yet"
                    hint="Adjustments you create will appear here."
                  />
                </Table.Td>
              </Table.Tr>
            )}
          </Table.Tbody>
        </Table>
      </Table.ScrollContainer>
      {data?.nextCursor && (
        <Group justify="center">
          <Button variant="subtle" onClick={() => setCursors((prev) => [...prev, data.nextCursor ?? undefined])}>
            Load more
          </Button>
        </Group>
      )}
    </Stack>
  );
}

function TransfersRegister({ canWrite }: { canWrite: boolean }) {
  const [cursors, setCursors] = useState<(string | undefined)[]>([undefined]);
  const cursor = cursors[cursors.length - 1];
  const { data, isLoading } = useTransfersList(cursor);

  return (
    <Stack gap="sm">
      <Group justify="flex-end">
        {canWrite && (
          <Button component={Link} to="/stores-transfers/transfers/new" leftSection={<IconPlus size={16} />}>
            New transfer
          </Button>
        )}
      </Group>
      <Table striped highlightOnHover verticalSpacing="xs">
        <Table.Thead>
          <Table.Tr>
            <Table.Th>Number</Table.Th>
            <Table.Th>Status</Table.Th>
            <Table.Th>Scope</Table.Th>
            <Table.Th>Lines</Table.Th>
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {(data?.items ?? []).map((transfer) => (
            <Table.Tr key={transfer.id}>
              <Table.Td>
                <Link to="/stores-transfers/transfers/$transferId" params={{ transferId: transfer.id }}>
                  {transfer.number}
                </Link>
              </Table.Td>
              <Table.Td>
                <DocStatusBadge status={transfer.status} />
              </Table.Td>
              <Table.Td>{transfer.fromSiteId === transfer.toSiteId ? 'Same-site' : 'Cross-site'}</Table.Td>
              <Table.Td>{transfer.lines.length}</Table.Td>
            </Table.Tr>
          ))}
          {!isLoading && (data?.items.length ?? 0) === 0 && (
            <Table.Tr>
              <Table.Td colSpan={4}>
                <EmptyState icon={IconExchangeOff} title="No transfers yet" hint="Transfers you create will appear here." />
              </Table.Td>
            </Table.Tr>
          )}
        </Table.Tbody>
      </Table>
      {data?.nextCursor && (
        <Group justify="center">
          <Button variant="subtle" onClick={() => setCursors((prev) => [...prev, data.nextCursor ?? undefined])}>
            Load more
          </Button>
        </Group>
      )}
    </Stack>
  );
}
